<!-- eslint-disable vue/prefer-true-attribute-shorthand -->
<template>
  <ais-search-box
    :class-names="{
      'ais-SearchBox': 'header-search__search-box',
    }"
  >
    <template #default="{ refine, currentRefinement }">
      <form
        v-if="view !== 'toggle-modal'"
        class="header-search__form"
        :class="{ 'header-search__form--mobile': view === 'mobile-form' }"
        :action="$variable('routes.search')"
        method="get"
        role="search"
      >
        <div
          class="header-search"
          :class="{
            'header-search--active': searchModalActive,
            'header-search--active-mobile': view === 'mobile-form',
          }"
        >
          <button
            class="header-search__expand"
            type="button"
            @click="openSearchModal($event, refine)"
            @mouseenter="prepareSearchModal(refine)"
            @touchstart="prepareSearchModal(refine)"
          >
            <icon-search />
          </button>

          <label
            class="visually-hidden"
            for="header-search-input"
            v-text="$string('predictive_search.placeholder')"
          />

          <input
            id="header-search-input"
            ref="searchRef"
            class="header-search__input text-body-s-regular"
            type="text"
            name="query"
            :placeholder="$string('predictive_search.placeholder')"
            required
            :value="getValue(currentRefinement)"
            @input="updateSearchQuery($event, refine)"
            @keydown="openSearchModal($event, refine)"
          >

          <button
            v-show="showClearButton"
            ref="searchClear"
            class="header-search__clear"
            type="button"
            @click="closeSearchModal(refine)"
          >
            <span
              class="visually-hidden"
              v-text="$string('predictive_search.close')"
            />

            <icon-close />
          </button>
        </div>
      </form>
      <button
        v-else
        class="header-search__expand"
        type="button"
        @click="toggleSearchModal($event, refine)"
        @mouseenter="prepareSearchModal(refine)"
        @touchstart="prepareSearchModal(refine)"
      >
        <span
          class="visually-hidden"
          v-text="$string('predictive_search.toggle')"
        />
        <icon-close v-if="searchModalActive" />
        <icon-search v-else />
      </button>
    </template>
  </ais-search-box>
</template>

<script>

/**
 * Vue: Search form (search-form)
 * -----------------------------------------------------------------------------
 * Search form component.
 *
 */
 import { mapActions, mapGetters, mapState } from 'vuex'
import { AisSearchBox } from 'vue-instantsearch/vue3/es'

import IconSearch from '~icons/misc/search.svg'
import IconClose from '~icons/misc/close.svg'

import { debounce } from '~/helpers/general'

export default {
  name: 'SearchForm',

  components: {
    AisSearchBox,
    IconSearch,
    IconClose,
  },

  props: {
    view: {
      // 'desktop-form', 'mobile-form' or 'toggle-modal'
      type: String,
      default: 'desktop-form',
    },
  },

  emits: ['update:query', 'focussed'],

  data() {
    return {
      input: '',
      // If user enters something in input.
      touched: false,
      searchModalActive: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      settings: (state) => state.index.settings,
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    namespace() {
      return this.view === 'desktop-form' ? 'search-modal' : 'search-modal search-modal-mobile'
    },

    showClearButton() {
      return this.view === 'desktop-form' ? this.searchModalActive : !!this.input
    },
  },

  watch: {

    /**
     * Watch active overlay to reset current link.
     */
    async getActiveOverlay() {
      await this.$nextTick()

      if (this.getActiveOverlay?.namespace === this.namespace) {
        this.searchModalActive = true
        return
      }

      this.searchModalActive = false
    },
  },

  created() {
    this.debouncedRefineResults = debounce(this.refineResults, this.settings.throttle)
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Allows the search query to be updated from the parent.
     * @param {Event} event - Input event.
     * @param {Function} refine - Algolia refine function.
     */
    updateSearchQuery(event, refine) {
      const value = event?.currentTarget?.value || ''
      this.input = value
      this.touched = true

      this.debouncedRefineResults(refine, value)
      this.$emit('update:query', value)
    },

    /**
     * Allows the search query to be updated from the parent.
     * @param {Function} refine - Algolia refine function.
     * @param {String} query - Input field value.
     */
    refineResults(refine, query) {
      refine(query)
    },

    getValue(currentRefinement) {

      /**
       * Before interaction with search box, set input from currentRefinement
       * and fire event search-active
       */
      if (!this.touched) {
        this.input = currentRefinement
      }

      return this.input
    },

    async prepareSearchModal(refine) {
      this.$emit('focussed')

      await this.$nextTick()
      refine(this.input || '')
      await this.$nextTick()
    },

    /**
     * Handles emitting an event when the input is newly focussed.
     * @param {Object} event - Focus event.
     * @param {Function} refine - Algolia refine function.
     */
    openSearchModal(event, refine) {
      const { key } = event

      /**
       * Do no open modal on tab and shift key press.
       */
      if (key === 'Tab' || key === 'Shift') {
        return
      }

      this.openOverlay({
        component: 'search-modal',
        ignoreDismissed: true,
        namespace: this.namespace,
      })
    },

    /**
     * Handles emitting an event when the input is newly focussed.
     * @param {Object} event - Focus event.
     * @param {Function} refine - Algolia refine function.
     */
    async toggleSearchModal(event, refine) {
      const { key } = event

      /**
       * Do no open modal on tab and shift key press.
       */
      if (key === 'Tab' || key === 'Shift') {
        return
      }

      this.$emit('focussed')

      await this.$nextTick()
      refine(this.input || '')
      await this.$nextTick()

      this.toggleOverlay({
        component: 'search-modal',
        ignoreDismissed: true,
        namespace: this.namespace,
      })
    },


    /**
     * Clear search input and close search modal.
     * @param {Function} refine - Algolia refine function.
     */
    closeSearchModal(refine) {
      this.input = ''
      this.$emit('update:query', '')
      if (this.view === 'desktop-form') {
        this.closeOverlay(this.namespace)
      }
      setTimeout(() => refine(''), this.settings.throttle)
    },
  },
}
</script>
